import { Divider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { ReactSortable, SortableEvent } from "react-sortablejs";
import { RoleMembersItem } from "../../../types/apiTypes";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { TriangleIcon } from "../../images";
import { RoleItemMember } from "./RoleItemMember";

const SHOW_MORE_THRESHOLD = 5;

type Props = {
  list: RoleMembersItem[];
  setList: (list: RoleMembersItem[]) => void;
  containerId?: string;
  isEditMode: boolean;
  updatedValues: { [key: string]: MembersUpdateValues };
  updateHandler: (
    values?: MembersUpdateValues,
    allValues?: { [key: string]: MembersUpdateValues },
  ) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
  onAdd: (e: SortableEvent) => void;
  onUpdate: (e: SortableEvent) => void;
  emptySlot?: React.ReactNode;
};

export const SortableGroupList = (props: Props) => {
  const {
    list,
    setList,
    containerId,
    isEditMode,
    updatedValues,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
    onAdd,
    onUpdate,
    emptySlot,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const hasHidden = list.length > SHOW_MORE_THRESHOLD;
  const hiddenCount = list.length - SHOW_MORE_THRESHOLD;
  const visibleList =
    hasHidden && !isExpanded ? list.slice(0, SHOW_MORE_THRESHOLD) : list;

  return (
    <Stack sx={{ width: "100%", padding: "8px" }}>
      <ReactSortable
        list={visibleList.map((el, index) => ({ ...el, index, chosen: true }))}
        setList={setList}
        animation={150}
        fallbackOnBody
        swapThreshold={0.65}
        ghostClass="ghost"
        forceFallback
        group={{ name: "shared", pull: true, put: true }}
        filter=".no-drag"
        style={{ display: "flex", flexDirection: "column", gap: "8px" }}
        onUpdate={onUpdate}
        onAdd={onAdd}
        id={containerId}
      >
        {visibleList.map((member) => (
          <RoleItemMember
            key={member.id}
            isEditMode={isEditMode}
            updatedValues={updatedValues[member.id]}
            updateHandler={updateHandler}
            setCommitteeSelected={setCommitteeSelected}
            setClusterSelected={setClusterSelected}
            {...member}
          />
        ))}
      </ReactSortable>

      {list.length === 0 && emptySlot}

      {hasHidden && (
        <>
          <Divider sx={{ margin: "4px 0" }} />
          <Stack
            className="no-drag"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              cursor: "pointer",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              padding: "8px 0 4px",
            }}
          >
            <Typography
              sx={{
                color: "var(--text-action-primary-default, #1C3C6C)",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {isExpanded ? "Show less" : "Show more"}
            </Typography>

            {!isExpanded && (
              <Stack
                sx={{
                  display: "flex",
                  padding: "0 6.5px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "100px",
                  background: "var(--Primary-default, #1C3C6C)",
                  minWidth: "20px",
                  height: "20px",
                }}
              >
                <Typography
                  sx={{
                    color: "#FFF",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "1",
                    display: "block",
                    marginTop: "1px",
                  }}
                >
                  {hiddenCount}
                </Typography>
              </Stack>
            )}

            <TriangleIcon color="#1C3C6C" rotate={isExpanded ? "180" : "0"} />
          </Stack>
        </>
      )}
    </Stack>
  );
};
